import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, FileSignature } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  trade_name: "", goods_services: "", abn: "", acn: "", director_names: "", mailing_address: "", phone: "",
  business_location: "", accounts_name: "", accounts_phone: "", accounts_email: "",
  bank: "", branch: "", bank_years: "", account_name: "", bsb: "", account_number: "",
  delivery_contact: "", delivery_phone: "", delivery_address: "", delivery_notes: "", signed_name: "",
};

const F = ({ label, k, form, set, required, type = "text" }) => (
  <div className="space-y-1">
    <p className="text-xs font-semibold">{label}{required && <span className="text-red-500"> *</span>}</p>
    <Input type={type} value={form[k]} onChange={(e) => set((p) => ({ ...p, [k]: e.target.value }))} data-testid={`acct-${k}`} />
  </div>
);

export default function AccountRequestDialog({ onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [agree, setAgree] = useState({ agree_terms: false, certify: false, guarantee: false });
  const [busy, setBusy] = useState(false);

  const ready = ["trade_name", "abn", "director_names", "mailing_address", "phone", "signed_name"].every((k) => form[k].trim()) && agree.agree_terms && agree.certify && agree.guarantee;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/account-requests", { ...form, ...agree });
      toast.success("Application submitted — Dave & Jo will review it shortly");
      setOpen(false); setForm(EMPTY); setAgree({ agree_terms: false, certify: false, guarantee: false });
      onSubmitted?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" variant="outline" data-testid="request-account-button" className="gap-2 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
          <FileSignature className="h-4 w-4" /> Request an account
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>14-day account application</DialogTitle>
          <DialogDescription>Apply for a monthly account with The Kilted Courier — invoiced at month-end, 14-day payment terms, $1,000 credit limit reviewed monthly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs font-bold uppercase tracking-wide text-kilted-green">Business details</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Trade name of company" k="trade_name" form={form} set={setForm} required />
            <F label="Goods / services you provide" k="goods_services" form={form} set={setForm} />
            <F label="ABN" k="abn" form={form} set={setForm} required />
            <F label="ACN" k="acn" form={form} set={setForm} />
            <F label="Director's name(s)" k="director_names" form={form} set={setForm} required />
            <F label="Phone" k="phone" form={form} set={setForm} required />
          </div>
          <F label="Mailing address" k="mailing_address" form={form} set={setForm} required />
          <F label="Business location (if different)" k="business_location" form={form} set={setForm} />

          <p className="text-xs font-bold uppercase tracking-wide text-kilted-green">Accounts contact</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <F label="Accounts manager" k="accounts_name" form={form} set={setForm} />
            <F label="Accounts phone" k="accounts_phone" form={form} set={setForm} />
            <F label="Accounts email" k="accounts_email" form={form} set={setForm} />
          </div>

          <p className="text-xs font-bold uppercase tracking-wide text-kilted-green">Bank details (optional)</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <F label="Bank" k="bank" form={form} set={setForm} />
            <F label="Branch" k="branch" form={form} set={setForm} />
            <F label="Years with bank" k="bank_years" form={form} set={setForm} />
            <F label="Account name" k="account_name" form={form} set={setForm} />
            <F label="BSB" k="bsb" form={form} set={setForm} />
            <F label="Account #" k="account_number" form={form} set={setForm} />
          </div>

          <p className="text-xs font-bold uppercase tracking-wide text-kilted-green">Delivery details for drivers</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Delivery contact person" k="delivery_contact" form={form} set={setForm} />
            <F label="Delivery contact phone" k="delivery_phone" form={form} set={setForm} />
          </div>
          <F label="Delivery address / special instructions" k="delivery_address" form={form} set={setForm} />
          <div className="space-y-1">
            <p className="text-xs font-semibold">Anything drivers should know</p>
            <Textarea rows={2} value={form.delivery_notes} onChange={(e) => setForm((p) => ({ ...p, delivery_notes: e.target.value }))} data-testid="acct-delivery_notes" />
          </div>

          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
            <b>Credit terms:</b> credit limit $1,000 (reviewed monthly) · invoices payable 14 days from invoice · interest on outstanding monies 2% per month (24% p.a.) · service may be halted if payment isn't received in full · The Kilted Courier may recover outstanding monies and associated costs · governed by ACT &amp; NSW law.
          </div>
          {[
            ["agree_terms", "I have read and agree to the Credit Terms, Conditional Sales Agreement and Collection Agreement above."],
            ["certify", "I certify the above information is accurate and complete, and authorise the release of credit-related information to The Kilted Courier."],
            ["guarantee", "As Director, I agree to be personally liable for any monies owed (personal guarantee)."],
          ].map(([k, t]) => (
            <label key={k} className="flex items-start gap-2 text-xs">
              <Checkbox checked={agree[k]} onCheckedChange={(v) => setAgree((p) => ({ ...p, [k]: !!v }))} data-testid={`acct-${k}`} className="mt-0.5" />
              <span>{t}</span>
            </label>
          ))}
          <F label="Type your full name as signature" k="signed_name" form={form} set={setForm} required />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !ready} data-testid="acct-submit" className="w-full bg-kilted-green hover:bg-kilted-greenLight sm:w-auto">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
